import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { doc, getDoc, updateDoc, getDocs, query, where, collection } from 'firebase/firestore';
import { db } from '../../firebase/config/firebase';
import { paymentApi, isGenericStudentName } from '../../services/paymentApi';
import { PaymentAttempt, Trip, StudentProfile, University, PaymentMethod } from '../../types';
import { adminBookingService } from '../../services/adminBookingService';
import { isReturnTrip } from '../../services/tripService';
import { universityService } from '../../services/universityService';
import { AdminDirectBookingModal, QueuedBookingItem } from './booking/AdminDirectBookingModal';
import { useAuth } from '../../auth/AuthContext';
import {
  CheckCircle2,
  XCircle,
  Eye,
  Clock,
  Search,
  Filter,
  RefreshCw,
  AlertTriangle,
  Building2,
  Phone,
  CreditCard,
  Calendar,
  Ticket,
  ExternalLink,
  Trash2,
  Edit,
  MessageSquare,
  UserCheck,
  BadgePercent,
} from 'lucide-react';
import { AdminLoadMore } from './AdminLoadMore';

const REJECTION_REASONS = [
  'الصورة غير واضحة',
  'المبلغ غير صحيح',
  'التحويل لم يصل',
  'رقم التحويل غير صحيح',
  'التحويل إلى حساب غير صحيح',
  'إثبات التحويل غير صالح',
  'أخرى',
];

interface AdminPaymentVerificationProps {
  onPendingCountChange?: (count: number) => void;
}

export const AdminPaymentVerification: React.FC<AdminPaymentVerificationProps> = ({
  onPendingCountChange,
}) => {
  const { user, staffProfile, isSuperAdmin } = useAuth();

  const currentAdminName = useMemo(() => {
    if (staffProfile?.fullName?.trim()) return staffProfile.fullName.trim();
    if (user?.displayName?.trim()) return user.displayName.trim();
    if (user?.email) {
      const local = user.email.split('@')[0];
      if (local.toLowerCase() === 'admin') return 'أدمن النظام الرئيسي';
      return local;
    }
    return isSuperAdmin ? 'أدمن النظام' : 'المشرف';
  }, [staffProfile, user, isSuperAdmin]);

  const currentAdminEmail = user?.email || staffProfile?.email || '';
  const currentAdminUid = user?.uid || '';

  const formatAuditDateTime = (isoDate?: string | null) => {
    if (!isoDate) return '—';
    try {
      const d = new Date(isoDate);
      if (isNaN(d.getTime())) return '—';
      const dateStr = d.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const timeStr = d.toLocaleTimeString('ar-EG', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
      return `${dateStr} (${timeStr})`;
    } catch {
      return '—';
    }
  };

  const [attempts, setAttempts] = useState<PaymentAttempt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'PENDING' | 'APPROVED' | 'REJECTED' | 'DELETED' | 'ALL'>('PENDING');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMethod, setSelectedMethod] = useState<string>('ALL');
  const [visibleCount, setVisibleCount] = useState<number>(15);

  // Modals state
  const [selectedAttempt, setSelectedAttempt] = useState<PaymentAttempt | null>(null);
  const [groupData, setGroupData] = useState<any | null>(null);
  const [discountAmount, setDiscountAmount] = useState<number | ''>('');
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isApproveConfirmOpen, setIsApproveConfirmOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [attemptToDelete, setAttemptToDelete] = useState<PaymentAttempt | null>(null);
  const [rejectionReason, setRejectionReason] = useState(REJECTION_REASONS[0]);
  const [adminNote, setAdminNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [modalActionError, setModalActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // Direct Booking Edit State
  const [isEditBookingOpen, setIsEditBookingOpen] = useState(false);
  const [allStudents, setAllStudents] = useState<StudentProfile[]>([]);
  const [allTrips, setAllTrips] = useState<Trip[]>([]);
  const [allUniversities, setAllUniversities] = useState<University[]>([]);
  const [editInitialStudent, setEditInitialStudent] = useState<StudentProfile | null>(null);
  const [editInitialDates, setEditInitialDates] = useState<string[]>([]);
  const [editInitialQueue, setEditInitialQueue] = useState<QueuedBookingItem[]>([]);
  const [editInitialTripId, setEditInitialTripId] = useState<string | undefined>(undefined);
  const [editInitialSeatNumber, setEditInitialSeatNumber] = useState<number | null>(null);
  const [editInitialPaymentMethod, setEditInitialPaymentMethod] = useState<PaymentMethod>('CASH');
  const [isPreparingEdit, setIsPreparingEdit] = useState(false);

  // Image Zoom State
  const [zoomLevel, setZoomLevel] = useState(1);

  // Load payment attempts with in-flight lock to prevent server congestion during request surges
  const isFetchingRef = useRef(false);
  const loadAttempts = useCallback(async (isSilent = false, forceRefresh = false) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    if (!isSilent) setIsLoading(true);
    setActionError(null);
    try {
      const res = await paymentApi.getAllPaymentAttempts(undefined, forceRefresh);
      if (res.success && res.data) {
        setAttempts(res.data);
        const pendingCount = res.data.filter(
          (a) => a.status === 'PENDING_VERIFICATION' || a.status === 'AWAITING_BRANCH_PAYMENT'
        ).length;
        if (onPendingCountChange) {
          onPendingCountChange(pendingCount);
        }
      } else if (!isSilent) {
        setActionError(res.error?.messageAr || 'تعذر تحميل سجلات الدفع.');
      }
    } catch (err: any) {
      if (!isSilent) setActionError(err?.message || 'حدث خطأ في الاتصال بقاعدة البيانات.');
    } finally {
      if (!isSilent) setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [onPendingCountChange]);

  useEffect(() => {
    loadAttempts();
    const timer = setInterval(() => {
      loadAttempts(true); // Silent background polling without UI flickering
    }, 25000);

    const handleSync = () => {
      loadAttempts(true, true);
    };
    window.addEventListener('oto_data_sync', handleSync);

    return () => {
      clearInterval(timer);
      window.removeEventListener('oto_data_sync', handleSync);
    };
  }, [loadAttempts]);

  // Tab Filtering, Search & Strict Chronological Ordering (Newest First)
  const filteredAttempts = useMemo(() => {
    const list = attempts.filter((attempt) => {
      // Tab filter
      let matchTab = true;
      if (activeTab === 'PENDING') {
        matchTab =
          attempt.status === 'PENDING_VERIFICATION' || attempt.status === 'AWAITING_BRANCH_PAYMENT';
      } else if (activeTab === 'APPROVED') {
        matchTab = attempt.status === 'PAID';
      } else if (activeTab === 'REJECTED') {
        matchTab = attempt.status === 'REJECTED';
      } else if (activeTab === 'DELETED') {
        matchTab = attempt.status === 'DELETED';
      } else if (activeTab === 'ALL') {
        matchTab = true;
      }

      // Method filter
      const matchMethod = selectedMethod === 'ALL' || attempt.paymentMethod === selectedMethod;

      // Search query filter
      const q = searchQuery.toLowerCase().trim();
      const matchSearch =
        q === '' ||
        (attempt.bookingCode && attempt.bookingCode.toLowerCase().includes(q)) ||
        (attempt.bookingGroupCode && attempt.bookingGroupCode.toLowerCase().includes(q)) ||
        (attempt.studentName && attempt.studentName.toLowerCase().includes(q)) ||
        (attempt.studentPhone && attempt.studentPhone.includes(q)) ||
        (attempt.transferFrom && attempt.transferFrom.includes(q)) ||
        (attempt.transferReference && attempt.transferReference.toLowerCase().includes(q)) ||
        (attempt.universityName && attempt.universityName.toLowerCase().includes(q));

      return matchTab && matchMethod && matchSearch;
    });

    // Strictly sort newest first (createdAt descending)
    return list.sort((a, b) => {
      const timeA = new Date(a.createdAt || a.submittedAt || 0).getTime();
      const timeB = new Date(b.createdAt || b.submittedAt || 0).getTime();
      return timeB - timeA;
    });
  }, [attempts, activeTab, selectedMethod, searchQuery]);

  // Sliced payment attempts for display (15 initial + 10 more on button click)
  const visibleAttempts = useMemo(() => {
    return filteredAttempts.slice(0, visibleCount);
  }, [filteredAttempts, visibleCount]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(15);
  }, [activeTab, searchQuery, selectedMethod]);

  // Counts for tabs
  const counts = useMemo(() => {
    const pending = attempts.filter(
      (a) => a.status === 'PENDING_VERIFICATION' || a.status === 'AWAITING_BRANCH_PAYMENT'
    ).length;
    const approved = attempts.filter((a) => a.status === 'PAID').length;
    const rejected = attempts.filter((a) => a.status === 'REJECTED').length;
    const deleted = attempts.filter((a) => a.status === 'DELETED').length;
    const all = attempts.length;
    return { pending, approved, rejected, deleted, all };
  }, [attempts]);

  // Calculation of original total, ticket count, discount per ticket, and net total
  const reviewCalc = useMemo(() => {
    if (!selectedAttempt) {
      return {
        ticketCount: 1,
        originalTotal: 0,
        originalUnitPrice: 0,
        discount: 0,
        discountPerTicket: 0,
        finalUnitPrice: 0,
        finalTotal: 0,
        hasDiscount: false,
        isExceeding: false,
      };
    }

    const ticketCount = Math.max(
      1,
      groupData?.bookings?.length ||
      groupData?.itemCount ||
      (selectedAttempt as any)?.tripCount ||
      1
    );

    const originalTotal = Number(
      selectedAttempt.originalAmount ||
      groupData?.totalAmount ||
      selectedAttempt.amount ||
      0
    );

    const originalUnitPrice = ticketCount > 0 ? Math.round(originalTotal / ticketCount) : originalTotal;
    const numDiscount = typeof discountAmount === 'number' ? Math.max(0, discountAmount) : 0;
    const isExceeding = numDiscount > originalTotal;
    const safeDiscount = Math.min(originalTotal, numDiscount);
    const hasDiscount = safeDiscount > 0;
    const discountPerTicket = ticketCount > 0 ? Number((safeDiscount / ticketCount).toFixed(2)) : safeDiscount;
    const finalUnitPrice = Math.max(0, Number((originalUnitPrice - discountPerTicket).toFixed(2)));
    const finalTotal = Math.max(0, originalTotal - safeDiscount);

    return {
      ticketCount,
      originalTotal,
      originalUnitPrice,
      discount: safeDiscount,
      discountPerTicket,
      finalUnitPrice,
      finalTotal,
      hasDiscount,
      isExceeding,
    };
  }, [selectedAttempt, groupData, discountAmount]);

  // Handle Approve Action
  const handleApprove = async () => {
    if (!selectedAttempt) return;
    if (reviewCalc.isExceeding) {
      const errMsg = `قيمة الخصم (${discountAmount} ج.م) لا يمكن أن تتجاوز إجمالي المبلغ (${reviewCalc.originalTotal} ج.م)!`;
      setActionError(errMsg);
      setModalActionError(errMsg);
      return;
    }

    setActionLoading(true);
    setActionError(null);
    setModalActionError(null);
    try {
      const numDiscount = reviewCalc.hasDiscount ? reviewCalc.discount : undefined;

      const res = await paymentApi.approveManualPayment({
        bookingId: selectedAttempt.bookingId,
        bookingGroupId: selectedAttempt.bookingGroupId || undefined,
        paymentAttemptId: selectedAttempt.id,
        adminName: currentAdminName,
        adminEmail: currentAdminEmail,
        adminUid: currentAdminUid,
        discountAmount: numDiscount,
      });

      if (res.success) {
        const nowIso = new Date().toISOString();
        const finalAmt = reviewCalc.finalTotal;
        const discountVal = reviewCalc.discount;

        // Optimistically update attempt
        setAttempts((prev) =>
          prev.map((a) =>
            a.id === selectedAttempt.id
              ? {
                  ...a,
                  status: 'PAID' as const,
                  amount: finalAmt,
                  originalAmount: reviewCalc.originalTotal,
                  discountAmount: discountVal,
                  discountPerTicket: reviewCalc.discountPerTicket,
                  discountApplied: reviewCalc.hasDiscount,
                  verifiedAt: nowIso,
                  reviewedAt: nowIso,
                  verifiedByName: currentAdminName,
                  reviewedByName: currentAdminName,
                  reviewedBy: currentAdminEmail || currentAdminUid,
                }
              : a
          )
        );

        const refCode = selectedAttempt.bookingCode || selectedAttempt.bookingGroupCode || selectedAttempt.bookingId || selectedAttempt.bookingGroupId || '';
        const discountMsg = reviewCalc.hasDiscount
          ? ` (مع تطبيق خصم إداري ${discountVal} ج.م وإدراج ${finalAmt} ج.م بالخزينة بواقع ${reviewCalc.finalUnitPrice} ج.م للتذكرة)`
          : ` (وإدراج ${finalAmt} ج.م بالخزينة)`;

        setActionSuccess(`تم اعتماد الدفع بنجاح للحجز رقم ${refCode} بواسطة "${currentAdminName}"${discountMsg}!`);
        setIsApproveConfirmOpen(false);
        setIsReviewOpen(false);
        setSelectedAttempt(null);
        setDiscountAmount('');
        await loadAttempts(true, true);
        setTimeout(() => setActionSuccess(null), 6000);
      } else {
        const errMsg = res.error?.messageAr || 'فشل اعتماد الدفع. يرجى المحاولة لاحقًا.';
        setActionError(errMsg);
        setModalActionError(errMsg);
      }
    } catch (err: any) {
      const errMsg = err?.message || 'حدث خطأ غير متوقع أثناء اعتماد الدفع.';
      setActionError(errMsg);
      setModalActionError(errMsg);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Reject Action
  const handleReject = async () => {
    if (!selectedAttempt) return;
    setActionLoading(true);
    setActionError(null);
    setModalActionError(null);
    try {
      const finalNote = adminNote.trim()
        ? `${rejectionReason} - ${adminNote.trim()}`
        : rejectionReason;

      const res = await paymentApi.rejectManualPayment({
        bookingId: selectedAttempt.bookingId,
        bookingGroupId: selectedAttempt.bookingGroupId || undefined,
        paymentAttemptId: selectedAttempt.id,
        rejectionReason: rejectionReason,
        adminNote: finalNote,
        adminName: currentAdminName,
        adminEmail: currentAdminEmail,
        adminUid: currentAdminUid,
      });

      if (res.success) {
        const nowIso = new Date().toISOString();
        // Optimistically update attempts in state
        setAttempts((prev) =>
          prev.map((a) =>
            a.id === selectedAttempt.id
              ? {
                  ...a,
                  status: 'REJECTED' as const,
                  rejectionReason,
                  adminNote: finalNote,
                  rejectedAt: nowIso,
                  reviewedAt: nowIso,
                  rejectedByName: currentAdminName,
                  reviewedByName: currentAdminName,
                  reviewedBy: currentAdminEmail || currentAdminUid,
                }
              : a
          )
        );

        setActionSuccess(`تم تسجيل رفض إثبات التحويل بواسطة "${currentAdminName}" بنجاح وإشعار الطالب.`);
        setIsRejectOpen(false);
        setIsReviewOpen(false);
        setSelectedAttempt(null);
        setAdminNote('');
        setModalActionError(null);

        if (onPendingCountChange) {
          onPendingCountChange(Math.max(0, counts.pending - 1));
        }

        await loadAttempts();
        setTimeout(() => setActionSuccess(null), 5000);
      } else {
        const errMsg = res.error?.messageAr || 'فشل رفض العملية. يرجى المحاولة لاحقًا.';
        setActionError(errMsg);
        setModalActionError(errMsg);
      }
    } catch (err: any) {
      const errMsg = err?.message || 'حدث خطأ غير متوقع أثناء تسجيل الرفض.';
      setActionError(errMsg);
      setModalActionError(errMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const promptDelete = (attempt: PaymentAttempt) => {
    setAttemptToDelete(attempt);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteAttempt = async (attempt: PaymentAttempt, permanent: boolean = false) => {
    if (!attempt) return;
    const refCode = attempt.bookingCode || attempt.bookingGroupCode || attempt.bookingId || attempt.id;

    // 1. Close modals
    setIsDeleteConfirmOpen(false);
    setAttemptToDelete(null);
    if (selectedAttempt?.id === attempt.id) {
      setIsReviewOpen(false);
      setSelectedAttempt(null);
    }

    if (permanent) {
      setAttempts((prev) =>
        prev.filter(
          (a) =>
            a.id !== attempt.id &&
            (!attempt.bookingCode || a.bookingCode !== attempt.bookingCode) &&
            (!attempt.bookingGroupCode || a.bookingGroupCode !== attempt.bookingGroupCode)
        )
      );
      setActionSuccess(`تم حذف الحجز وسجل الدفع (${refCode}) نهائياً وتحرير المقاعد.`);
    } else {
      // Transition item smoothly to DELETED status so it moves to "الطلبات المحذوفة"
      setAttempts((prev) =>
        prev.map((a) => {
          if (
            a.id === attempt.id ||
            (attempt.bookingCode && a.bookingCode === attempt.bookingCode) ||
            (attempt.bookingGroupCode && a.bookingGroupCode === attempt.bookingGroupCode)
          ) {
            return {
              ...a,
              status: 'DELETED' as const,
              deletedAt: new Date().toISOString(),
              deletedBy: 'ADMIN',
            };
          }
          return a;
        })
      );
      setActionSuccess(`تم نقل طلب الحجز (${refCode}) إلى قسم الطلبات المحذوفة بنجاح وتحرير المقاعد.`);
    }

    // 2. Perform backend update & seat release
    setActionLoading(true);
    setActionError(null);
    try {
      await paymentApi.deletePaymentAttempt({
        paymentAttemptId: attempt.id,
        bookingId: attempt.bookingId,
        bookingGroupId: attempt.bookingGroupId,
        bookingCode: attempt.bookingCode,
        bookingGroupCode: attempt.bookingGroupCode,
        tripId: attempt.tripId,
        seatNumber: attempt.seatNumber,
        permanent,
      });

      await loadAttempts(false, true);
      window.dispatchEvent(new CustomEvent('oto_data_sync'));
    } catch (err: any) {
      console.warn('Background delete error:', err);
    } finally {
      setActionLoading(false);
      setTimeout(() => setActionSuccess(null), 5000);
    }
  };

  const openReview = async (attempt: PaymentAttempt) => {
    setSelectedAttempt(attempt);
    setDiscountAmount(attempt.discountAmount && attempt.discountAmount > 0 ? attempt.discountAmount : '');
    setZoomLevel(1);
    setIsReviewOpen(true);

    // Deep enrichment if anything is missing or if name is generic
    const needsEnrichment =
      !attempt.studentName ||
      isGenericStudentName(attempt.studentName) ||
      !attempt.studentPhone ||
      !attempt.amount ||
      !attempt.universityName ||
      attempt.bookingGroupId ||
      !attempt.proofImageUrl;

    if (needsEnrichment) {
      try {
        let enriched = { ...attempt };
        if (attempt.bookingGroupId) {
          const gSnap = await getDoc(doc(db, 'bookingGroups', attempt.bookingGroupId));
          if (gSnap.exists()) {
            const g = gSnap.data() as any;

            // Deep enrichment: Fetch live child bookings from bookings collection to ensure 100% accurate seat numbers and directions
            try {
              const bQuery = query(collection(db, 'bookings'), where('bookingGroupId', '==', attempt.bookingGroupId));
              const bDocsSnap = await getDocs(bQuery);
              if (!bDocsSnap.empty) {
                const liveBookings = bDocsSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
                liveBookings.sort(
                  (a, b) =>
                    (a.bookingSequence || 0) - (b.bookingSequence || 0) ||
                    new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
                );
                g.bookings = liveBookings.map((lb: any) => {
                  const isRet = isReturnTrip(lb.direction) || (typeof lb.tripId === 'string' && lb.tripId.includes('_return_'));
                  return {
                    ...lb,
                    bookingCode: lb.bookingCode,
                    ticketId: lb.ticketId,
                    date: lb.tripDate || lb.date,
                    time: lb.departureTime || lb.time,
                    seatNumber: lb.seatNumber,
                    direction: isRet ? 'RETURN' : 'OUTBOUND',
                    origin: lb.origin,
                    destination: lb.destination,
                    price: lb.price,
                  };
                });
              }
            } catch (liveErr) {
              console.warn('Failed to fetch live child bookings for review modal:', liveErr);
            }

            setGroupData(g);
            if (g.studentSnapshot?.fullName && !isGenericStudentName(g.studentSnapshot.fullName)) {
              enriched.studentName = g.studentSnapshot.fullName;
            }
            enriched.studentPhone = g.studentSnapshot?.phoneNumber || enriched.studentPhone || '';
            enriched.universityName = g.studentSnapshot?.universityName || g.universityName || enriched.universityName || 'الجامعة المحددة';
            enriched.tripDate = g.tripDates?.join(' ، ') || g.bookings?.map((b: any) => b.date).join(' ، ') || enriched.tripDate;
            enriched.seatNumber = (g.bookings?.map((b: any) => '#' + b.seatNumber).join(', ') as any) || (g.itemCount ? `${g.itemCount} مقاعد` : enriched.seatNumber);
            enriched.amount = g.totalAmount || g.price || enriched.amount || 0;
            enriched.bookingGroupCode = g.groupCode || enriched.bookingGroupCode;
            enriched.bookingCode = g.groupCode || enriched.bookingCode;
            enriched.proofImageUrl = enriched.proofImageUrl || g.proofImageUrl || g.proofImageBase64 || null;
            (enriched as any).proofImageBase64 = (enriched as any).proofImageBase64 || g.proofImageBase64 || g.proofImageUrl || null;
          }
        } else if (attempt.bookingId) {
          setGroupData(null);
          const bSnap = await getDoc(doc(db, 'bookings', attempt.bookingId));
          if (bSnap.exists()) {
            const b = bSnap.data() as any;
            if (b.studentSnapshot?.fullName && !isGenericStudentName(b.studentSnapshot.fullName)) {
              enriched.studentName = b.studentSnapshot.fullName;
            } else if (b.studentName && !isGenericStudentName(b.studentName)) {
              enriched.studentName = b.studentName;
            }
            enriched.studentPhone = b.studentSnapshot?.phoneNumber || b.phoneNumber || enriched.studentPhone;
            enriched.universityName = b.universityName || enriched.universityName;
            enriched.tripDate = b.tripDate || enriched.tripDate;
            enriched.seatNumber = b.seatNumber || enriched.seatNumber;
            enriched.amount = b.price || enriched.amount || 0;
            enriched.bookingCode = b.bookingCode || enriched.bookingCode;
            enriched.proofImageUrl = enriched.proofImageUrl || b.proofImageUrl || b.proofImageBase64 || null;
            (enriched as any).proofImageBase64 = (enriched as any).proofImageBase64 || b.proofImageBase64 || b.proofImageUrl || null;
          }
        } else {
          setGroupData(null);
        }

        // If name is still generic, resolve from students collection
        if ((!enriched.studentName || isGenericStudentName(enriched.studentName) || !enriched.studentPhone) && enriched.studentId) {
          try {
            const sSnap = await getDoc(doc(db, 'students', enriched.studentId));
            if (sSnap.exists()) {
              const sData = sSnap.data() as any;
              if (sData.fullName && !isGenericStudentName(sData.fullName)) {
                enriched.studentName = sData.fullName.trim();
              }
              if (!enriched.studentPhone && sData.phoneNumber) {
                enriched.studentPhone = sData.phoneNumber;
              }
            }
          } catch (_) {}
        }

        // Final fallback format
        if (!enriched.studentName || isGenericStudentName(enriched.studentName)) {
          enriched.studentName = enriched.studentPhone
            ? `طالب (${enriched.studentPhone})`
            : enriched.bookingCode
            ? `طالب (${enriched.bookingCode})`
            : 'طالب مسجل';
        }

        // Persist healed name to Firestore paymentAttempts asynchronously
        if (enriched.studentName && !isGenericStudentName(enriched.studentName) && enriched.studentName !== attempt.studentName) {
          updateDoc(doc(db, 'paymentAttempts', attempt.id), {
            studentName: enriched.studentName,
            ...(enriched.studentPhone ? { studentPhone: enriched.studentPhone } : {}),
          }).catch(() => {});

          setAttempts((prev) =>
            prev.map((a) => (a.id === attempt.id ? { ...a, studentName: enriched.studentName, studentPhone: enriched.studentPhone } : a))
          );
        }

        setSelectedAttempt(enriched);
      } catch (err) {
        console.warn('Error enriching selectedAttempt in modal:', err);
      }
    }
  };

  // Ensure students, trips, universities are loaded for edit mode
  const ensureEditDependencies = useCallback(async () => {
    try {
      const [stds, trps, unis] = await Promise.all([
        allStudents.length === 0 ? adminBookingService.getAllStudents() : Promise.resolve(allStudents),
        allTrips.length === 0 ? adminBookingService.getAllTrips() : Promise.resolve(allTrips),
        allUniversities.length === 0 ? universityService.getAllUniversities() : Promise.resolve(allUniversities),
      ]);
      if (allStudents.length === 0) setAllStudents(stds);
      if (allTrips.length === 0) setAllTrips(trps);
      if (allUniversities.length === 0) setAllUniversities(unis);
      return { stds, trps, unis };
    } catch (e) {
      console.warn('Could not load edit dependencies:', e);
      return { stds: allStudents, trps: allTrips, unis: allUniversities };
    }
  }, [allStudents, allTrips, allUniversities]);

  useEffect(() => {
    ensureEditDependencies().catch(() => {});
  }, [ensureEditDependencies]);

  const handleOpenEditBooking = async (attempt: PaymentAttempt) => {
    setIsPreparingEdit(true);
    try {
      const { stds, trps } = await ensureEditDependencies();

      // 1. Resolve student by UID or phone number
      let targetStudent: StudentProfile | null =
        stds.find(
          (s) =>
            (attempt.studentId && s.uid === attempt.studentId) ||
            (attempt.studentPhone &&
              s.phoneNumber &&
              s.phoneNumber.replace(/\D/g, '') === attempt.studentPhone.replace(/\D/g, ''))
        ) || null;

      if (!targetStudent) {
        targetStudent = {
          uid: attempt.studentId && attempt.studentId !== 'unknown' ? attempt.studentId : `std_${Date.now()}`,
          fullName:
            attempt.studentName && !isGenericStudentName(attempt.studentName)
              ? attempt.studentName
              : 'طالب مسجل',
          phoneNumber: attempt.studentPhone || '',
          email: (attempt as any).studentEmail || 'student@otostudents.com',
          address: 'الإسكندرية',
          universityGovernorate: 'الإسكندرية',
          universityName: attempt.universityName || 'الجامعة المعتمدة',
          universityId: '',
          status: 'ACTIVE',
          profileCompleted: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      }
      setEditInitialStudent(targetStudent);

      // 2. Parse dates
      const rawDateStr = attempt.tripDate || '';
      const dateTokens = rawDateStr.split(/[،,]/).map((s) => s.trim()).filter(Boolean);
      const parsedDates: string[] = [];
      for (const dt of dateTokens) {
        if (/^\d{2}-\d{2}-\d{4}$/.test(dt)) {
          const [d, m, y] = dt.split('-');
          parsedDates.push(`${y}-${m}-${d}`);
        } else if (/^\d{4}-\d{2}-\d{2}$/.test(dt)) {
          parsedDates.push(dt);
        }
      }
      if (parsedDates.length === 0) {
        parsedDates.push(new Date().toISOString().slice(0, 10));
      }
      setEditInitialDates(parsedDates);

      // 3. Pre-populate queue if booking group has multiple bookings
      const queueItems: QueuedBookingItem[] = [];
      const bookingsList = groupData?.bookings || (attempt as any).bookings;
      if (bookingsList && Array.isArray(bookingsList) && bookingsList.length > 0) {
        for (let idx = 0; idx < bookingsList.length; idx++) {
          const gb = bookingsList[idx];
          const gbDate = /^\d{2}-\d{2}-\d{4}$/.test(gb.date)
            ? gb.date.split('-').reverse().join('-')
            : gb.date;
          const matchedTrip =
            trps.find((t) => t.id === gb.tripId) ||
            trps.find(
              (t) =>
                t.date === gbDate &&
                t.direction === (gb.direction || 'OUTBOUND') &&
                (!gb.time || t.departureTime === gb.time)
            ) ||
            trps.find((t) => t.date === gbDate);

          if (matchedTrip) {
            queueItems.push({
              id: `edit-queue-${idx}-${matchedTrip.id}-${Date.now()}`,
              trip: matchedTrip,
              date: gbDate,
              direction: (gb.direction as 'OUTBOUND' | 'RETURN') || 'OUTBOUND',
              departureTime: gb.time || matchedTrip.departureTime,
              universityName: gb.universityName || matchedTrip.universityName || attempt.universityName,
              price: gb.price || matchedTrip.price || 0,
              seatNumber: typeof gb.seatNumber === 'number' ? gb.seatNumber : 1,
            });
          }
        }
      }
      setEditInitialQueue(queueItems);

      // 4. Single trip fallback
      let singleTripId: string | undefined = undefined;
      if (queueItems.length === 0) {
        const firstDate = parsedDates[0];
        const foundTrip = trps.find((t) => t.date === firstDate && t.status !== 'CANCELLED');
        if (foundTrip) singleTripId = foundTrip.id;
      }
      setEditInitialTripId(singleTripId);

      const parsedSeat =
        typeof attempt.seatNumber === 'number'
          ? attempt.seatNumber
          : parseInt(String(attempt.seatNumber).replace(/\D/g, ''), 10);
      setEditInitialSeatNumber(!isNaN(parsedSeat) && parsedSeat > 0 ? parsedSeat : null);

      const method = (attempt.paymentMethod as PaymentMethod) || 'CASH';
      setEditInitialPaymentMethod(method);

      setIsEditBookingOpen(true);
    } catch (err) {
      console.warn('Error opening edit booking modal:', err);
      alert('تعذر تحميل بيانات الرحلات للتعديل، يرجى المحاولة مرة أخرى.');
    } finally {
      setIsPreparingEdit(false);
    }
  };

  // Detect duplicate attempts for same student and trip date to alert admin
  const duplicateAttemptIds = useMemo(() => {
    const dupes = new Set<string>();
    const seen = new Map<string, string>(); // key -> attemptId

    attempts.forEach((a) => {
      if (a.status === 'DELETED') return;
      const cleanPhone = (a.studentPhone || '').replace(/\D/g, '');
      const cleanName = (a.studentName || '').trim().toLowerCase();
      const identifier = cleanPhone || (cleanName && !isGenericStudentName(cleanName) ? cleanName : null);
      if (!identifier || !a.tripDate) return;

      const key = `${identifier}_${a.tripDate}`;
      if (seen.has(key)) {
        dupes.add(a.id);
        const firstId = seen.get(key);
        if (firstId) dupes.add(firstId);
      } else {
        seen.set(key, a.id);
      }
    });

    return dupes;
  }, [attempts]);

  // WhatsApp confirmation generator with full trip and booking details
  const getBookingConfirmationWhatsAppUrl = (
    attempt: PaymentAttempt,
    group?: any
  ): string => {
    if (!attempt.studentPhone) return '#';

    const studentName =
      attempt.studentName && !isGenericStudentName(attempt.studentName)
        ? attempt.studentName
        : 'طالبنا العزيز';

    const bookingCode =
      attempt.bookingCode ||
      attempt.bookingGroupCode ||
      (attempt.bookingId ? attempt.bookingId.slice(0, 8) : '—');

    const university = attempt.universityName || 'الجامعة المعتمدة';
    const tripDate = attempt.tripDate || '—';
    const seatInfo = attempt.seatNumber
      ? typeof attempt.seatNumber === 'number'
        ? `#${attempt.seatNumber}`
        : String(attempt.seatNumber)
      : '—';
    const amount = attempt.amount || 0;

    let methodLabel = 'سداد نقدي بالفرع';
    if (attempt.paymentMethod === 'INSTAPAY') {
      methodLabel = 'إنستاباي (InstaPay)';
    } else if (attempt.paymentMethod === 'WALLET') {
      methodLabel = `محفظة إلكترونية (${attempt.paymentProvider || 'فودافون كاش / أخرى'})`;
    }

    let msg = `مرحباً يا ${studentName} 👋\n`;
    msg += `تم اعتماد وتأكيد حجز رحلتك بنجاح مع OTO Students! 🚌✨\n\n`;
    msg += `📋 *بيانات الحجز المؤكد:*\n`;
    msg += `• كود الحجز: *${bookingCode}*\n`;
    msg += `• الوجهة: ${university}\n`;
    msg += `• تاريخ الرحلة: ${tripDate}\n`;
    msg += `• رقم المقعد: ${seatInfo}\n`;
    msg += `• طريقة السداد: ${methodLabel}\n`;
    msg += `• المبلغ الإجمالي: ${amount} ج.م\n`;
    msg += `• حالة الحجز: *معتمد ومؤكد ✓*\n\n`;

    if (group?.bookings && Array.isArray(group.bookings) && group.bookings.length > 1) {
      msg += `🗂️ *تفاصيل الرحلات المحجوزة (${group.bookings.length} رحلات):*\n`;
      group.bookings.forEach((b: any, idx: number) => {
        const dir = b.direction === 'RETURN' ? 'عودة للإسكندرية 🌙' : 'ذهاب للجامعة ☀️';
        msg += `  ${idx + 1}. ${dir}: ${b.date} الساعة ${b.time || '—'} (مقعد #${b.seatNumber || '—'})\n`;
      });
      msg += `\n`;
    }

    msg += `🎫 يمكنك استعراض تذكرتك الرقمية ومسار الحافلة ومحطة الركوب عبر منصة الطلاب:\n`;
    msg += `https://otostudents.com\n\n`;
    msg += `نتمنى لك فصلاً دراسياً موفقاً ورحلات آمنة ومريحة دائماً مع OTO! 🌟`;

    return adminBookingService.getWhatsAppUrl(attempt.studentPhone, msg);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING_VERIFICATION':
        return <span className="admin-status-badge pending">بانتظار المراجعة</span>;
      case 'AWAITING_BRANCH_PAYMENT':
        return <span className="admin-status-badge branch">دفع بالفرع</span>;
      case 'PAID':
        return <span className="admin-status-badge approved">معتمد ومؤكد ✓</span>;
      case 'REJECTED':
        return <span className="admin-status-badge rejected">مرفوض ✕</span>;
      case 'DELETED':
        return (
          <span
            className="admin-status-badge deleted"
            style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #CBD5E1' }}
          >
            محذوف 🗑️
          </span>
        );
      default:
        return <span className="admin-status-badge">{status}</span>;
    }
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'INSTAPAY':
        return (
          <span title="InstaPay">
            <Phone size={16} className="text-purple-600" />
          </span>
        );
      case 'WALLET':
        return (
          <span title="Mobile Wallet">
            <CreditCard size={16} className="text-emerald-600" />
          </span>
        );
      case 'BRANCH':
        return (
          <span title="Branch Payment">
            <Building2 size={16} className="text-[#E5232A]" />
          </span>
        );
      default:
        return <CreditCard size={16} />;
    }
  };

  return (
    <div className="admin-verification-container" dir="rtl">
      {/* Page Header */}
      <div className="admin-page-header">
        <div>
          <h2 className="admin-page-title">مراجعة واعتماد المدفوعات اليدوية</h2>
          <p className="admin-page-subtitle">
            التحقق من إثباتات تحويل إنستاباي والمحافظ الإلكترونية وتأكيد سداد الفرع لإصدار تذاكر الطلاب.
          </p>
        </div>
        <button
          className="admin-btn-refresh flex items-center gap-1.5"
          onClick={() => loadAttempts(false)}
          disabled={isLoading}
        >
          <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          <span>تحديث القائمة</span>
        </button>
      </div>

      {/* Global Action Messages */}
      {actionSuccess && (
        <div className="admin-alert-banner success" role="status">
          <CheckCircle2 size={18} />
          <span>{actionSuccess}</span>
        </div>
      )}
      {actionError && (
        <div className="admin-alert-banner error" role="alert">
          <AlertTriangle size={18} />
          <span>{actionError}</span>
        </div>
      )}

      {/* Tabs Row */}
      <div className="admin-tabs-bar">
        <button
          className={`admin-tab-btn ${activeTab === 'PENDING' ? 'active' : ''}`}
          onClick={() => setActiveTab('PENDING')}
        >
          <Clock size={16} />
          <span>طلبات قيد المراجعة</span>
          <span className="tab-counter-pill">{counts.pending}</span>
        </button>

        <button
          className={`admin-tab-btn ${activeTab === 'APPROVED' ? 'active' : ''}`}
          onClick={() => setActiveTab('APPROVED')}
        >
          <CheckCircle2 size={16} />
          <span>المدفوعات المعتمدة</span>
          <span className="tab-counter-pill green">{counts.approved}</span>
        </button>

        <button
          className={`admin-tab-btn ${activeTab === 'REJECTED' ? 'active' : ''}`}
          onClick={() => setActiveTab('REJECTED')}
        >
          <XCircle size={16} />
          <span>الطلبات المرفوضة</span>
          <span className="tab-counter-pill red">{counts.rejected}</span>
        </button>

        <button
          className={`admin-tab-btn ${activeTab === 'DELETED' ? 'active' : ''}`}
          onClick={() => setActiveTab('DELETED')}
        >
          <Trash2 size={16} />
          <span>الطلبات المحذوفة</span>
          <span className="tab-counter-pill grey">{counts.deleted}</span>
        </button>

        <button
          className={`admin-tab-btn ${activeTab === 'ALL' ? 'active' : ''}`}
          onClick={() => setActiveTab('ALL')}
        >
          <span>كل العمليات</span>
          <span className="tab-counter-pill grey">{counts.all}</span>
        </button>
      </div>

      {/* Filters Bar */}
      <div className="admin-filters-bar">
        <div className="admin-search-box">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="بحث بكود الحجز، اسم الطالب، رقم المحول منه، أو الجامعة..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-btn" onClick={() => setSearchQuery('')}>
              ✕
            </button>
          )}
        </div>

        <div className="admin-select-wrapper">
          <Filter size={16} />
          <select
            value={selectedMethod}
            onChange={(e) => setSelectedMethod(e.target.value)}
            className="admin-select"
          >
            <option value="ALL">جميع طرق الدفع</option>
            <option value="INSTAPAY">إنستاباي (InstaPay)</option>
            <option value="WALLET">محفظة إلكترونية (Wallet)</option>
            <option value="BRANCH">الدفع من الفرع (Branch)</option>
          </select>
        </div>
      </div>

      {/* Table Section */}
      <div className="admin-table-card">
        {isLoading ? (
          <div className="admin-table-loading">
            <RefreshCw size={28} className="animate-spin text-[#E5232A]" />
            <p>جاري تحميل سجلات الدفع والتحقق...</p>
          </div>
        ) : filteredAttempts.length === 0 ? (
          <div className="admin-empty-state">
            <CheckCircle2 size={42} className="empty-icon text-slate-400" />
            <h3>لا توجد طلبات تطابق معايير البحث الحالية</h3>
            <p>
              {activeTab === 'PENDING'
                ? 'لا توجد طلبات دفع معلقة تحتاج للمراجعة حالياً. عمل رائع!'
                : 'لم يتم العثور على عمليات في هذا القسم.'}
            </p>
          </div>
        ) : (
          <>
            <div className="admin-table-responsive admin-desktop-table-view">
              <table className="admin-table">
              <thead>
                <tr>
                  <th>كود الحجز</th>
                  <th>الطالب</th>
                  <th>الجامعة والرحلة</th>
                  <th>المقعد</th>
                  <th>المبلغ</th>
                  <th>طريقة الدفع</th>
                  <th>رقم التحويل</th>
                  <th>تاريخ الإرسال</th>
                  <th>الحالة</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {visibleAttempts.map((attempt) => (
                  <tr key={attempt.id} className={attempt.status === 'PENDING_VERIFICATION' ? 'row-pending' : ''}>
                    {/* Booking Code */}
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        <span className="font-mono font-bold text-[#E5232A]">
                          {attempt.bookingCode || attempt.bookingGroupCode || (attempt.bookingId ? attempt.bookingId.slice(0, 8) : attempt.bookingGroupId ? attempt.bookingGroupId.slice(0, 8) : '—')}
                        </span>
                        {(attempt.bookingGroupId || (attempt as any).tripCount > 1) && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px',
                              background: '#EFF6FF',
                              color: '#1D4ED8',
                              border: '1px solid #BFDBFE',
                              borderRadius: '999px',
                              padding: '1px 7px',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              width: 'fit-content',
                            }}
                          >
                            🗂️ {(attempt as any).tripCount || 8} رحلات
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Student */}
                    <td>
                      <div className="cell-student-info">
                        <strong>
                          {attempt.studentName && !isGenericStudentName(attempt.studentName)
                            ? attempt.studentName
                            : attempt.studentPhone
                            ? `طالب (${attempt.studentPhone})`
                            : 'طالب مسجل'}
                        </strong>
                        <span className="text-muted text-xs">{attempt.studentPhone || '—'}</span>
                        {duplicateAttemptIds.has(attempt.id) && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px',
                              background: '#FEF3C7',
                              color: '#92400E',
                              border: '1px solid #FCD34D',
                              borderRadius: '4px',
                              padding: '1px 5px',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              marginTop: '2px',
                              width: 'fit-content',
                            }}
                            title="يوجد أكثر من حجز مسجل لهذا الطالب في نفس اليوم (تكرار)"
                          >
                            <AlertTriangle size={10} />
                            حجز مكرر
                          </span>
                        )}
                      </div>
                    </td>

                    {/* University & Trip */}
                    <td>
                      <div className="cell-trip-info">
                        <span>{attempt.universityName || 'الجامعة'}</span>
                        <span className="text-muted text-xs flex items-center gap-1">
                          <Calendar size={12} />
                          {attempt.tripDate || '—'}
                        </span>
                      </div>
                    </td>

                    {/* Seat */}
                    <td>
                      {(attempt.bookingGroupId || (attempt as any).tripCount > 1) ? (
                        <span className="seat-badge" style={{ background: '#F8FAFC', color: '#1E293B', border: '1px solid #CBD5E1', fontSize: '0.78rem' }}>
                          {(attempt as any).tripCount || 8} مقاعد
                        </span>
                      ) : (
                        <span className="seat-badge">#{attempt.seatNumber || '—'}</span>
                      )}
                    </td>

                    {/* Amount */}
                    <td>
                      <strong className="text-slate-800">{attempt.amount} ج.م</strong>
                      {attempt.discountAmount && attempt.discountAmount > 0 ? (
                        <span
                          className="block text-[11px] text-emerald-700 font-bold bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200 mt-0.5"
                          title={`خصم إداري: ${attempt.discountAmount} ج.م`}
                        >
                          خصم {attempt.discountAmount} ج
                        </span>
                      ) : null}
                    </td>

                    {/* Payment Method */}
                    <td>
                      <span className="method-pill flex items-center gap-1">
                        {getMethodIcon(attempt.paymentMethod)}
                        <span>
                          {attempt.paymentMethod === 'INSTAPAY'
                            ? 'InstaPay'
                            : attempt.paymentMethod === 'WALLET'
                            ? attempt.paymentProvider || 'محفظة'
                            : 'الفرع'}
                        </span>
                      </span>
                    </td>

                    {/* Transfer From */}
                    <td>
                      <span className="font-mono text-sm">
                        {attempt.transferFrom || (attempt.paymentMethod === 'BRANCH' ? 'سداد نقدي' : '—')}
                      </span>
                    </td>

                    {/* Submitted At */}
                    <td>
                      <span className="text-xs text-muted">
                        {attempt.submittedAt
                          ? new Date(attempt.submittedAt).toLocaleDateString('ar-EG', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </span>
                    </td>

                    {/* Status */}
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                        {getStatusBadge(attempt.status)}

                        {/* Approved Audit Info */}
                        {attempt.status === 'PAID' && (attempt.verifiedByName || attempt.reviewedByName || attempt.verifiedAt || attempt.reviewedAt) && (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px',
                              background: '#F0FDF4',
                              border: '1px solid #BBF7D0',
                              borderRadius: '6px',
                              padding: '3px 6px',
                              fontSize: '10.5px',
                              color: '#166534',
                              lineHeight: 1.25,
                              textAlign: 'right',
                              maxWidth: '180px',
                            }}
                            title={`تم اعتماد الدفع بواسطة ${attempt.verifiedByName || attempt.reviewedByName || 'الإدارة'}`}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px', fontWeight: 700 }}>
                              <UserCheck size={11} style={{ color: '#16A34A', flexShrink: 0 }} />
                              <span>اعتمد: {attempt.verifiedByName || attempt.reviewedByName || 'الإدارة'}</span>
                            </div>
                            {(attempt.verifiedAt || attempt.reviewedAt) && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#15803D', fontSize: '10px' }}>
                                <Clock size={10} style={{ flexShrink: 0 }} />
                                <span dir="rtl">{formatAuditDateTime(attempt.verifiedAt || attempt.reviewedAt)}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Rejected Audit Info */}
                        {attempt.status === 'REJECTED' && (attempt.rejectedByName || attempt.reviewedByName || attempt.rejectedAt || attempt.reviewedAt) && (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px',
                              background: '#FEF2F2',
                              border: '1px solid #FECDD3',
                              borderRadius: '6px',
                              padding: '3px 6px',
                              fontSize: '10.5px',
                              color: '#991B1B',
                              lineHeight: 1.25,
                              textAlign: 'right',
                              maxWidth: '180px',
                            }}
                            title={`تم رفض الطلب بواسطة ${attempt.rejectedByName || attempt.reviewedByName || 'الإدارة'}`}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px', fontWeight: 700 }}>
                              <XCircle size={11} style={{ color: '#DC2626', flexShrink: 0 }} />
                              <span>رُفض: {attempt.rejectedByName || attempt.reviewedByName || 'الإدارة'}</span>
                            </div>
                            {(attempt.rejectedAt || attempt.reviewedAt) && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#B91C1C', fontSize: '10px' }}>
                                <Clock size={10} style={{ flexShrink: 0 }} />
                                <span dir="rtl">{formatAuditDateTime(attempt.rejectedAt || attempt.reviewedAt)}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="action-buttons-group">
                        <button
                          className="btn-action review"
                          onClick={() => openReview(attempt)}
                          title="مراجعة الإثبات والتفاصيل"
                        >
                          <Eye size={15} />
                          <span>مراجعة</span>
                        </button>

                        {/* Direct WhatsApp button for confirmed booking */}
                        {attempt.status === 'PAID' && attempt.studentPhone && (
                          <a
                            href={getBookingConfirmationWhatsAppUrl(attempt)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-action whatsapp hover:bg-emerald-100 text-emerald-700 border-emerald-300"
                            title="إرسال تأكيد الحجز والبيانات للطالب عبر واتساب"
                            style={{
                              backgroundColor: '#ECFDF5',
                              color: '#059669',
                              borderColor: '#A7F3D0',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '5px 8px',
                              borderRadius: '6px',
                              textDecoration: 'none',
                              gap: '4px',
                              fontSize: '0.78rem',
                              fontWeight: 700,
                            }}
                          >
                            <MessageSquare size={15} />
                            <span>واتساب</span>
                          </a>
                        )}

                        {(attempt.status === 'PENDING_VERIFICATION' ||
                          attempt.status === 'AWAITING_BRANCH_PAYMENT') && (
                          <>
                            <button
                              className="btn-action approve"
                              onClick={() => {
                                setSelectedAttempt(attempt);
                                setIsApproveConfirmOpen(true);
                              }}
                              title="اعتماد الدفع فوراً"
                            >
                              <CheckCircle2 size={15} />
                            </button>

                            <button
                              className="btn-action reject"
                              onClick={() => {
                                setSelectedAttempt(attempt);
                                setRejectionReason(REJECTION_REASONS[0]);
                                setAdminNote('');
                                setIsRejectOpen(true);
                              }}
                              title="رفض الإثبات"
                            >
                              <XCircle size={15} />
                            </button>
                          </>
                        )}

                        <button
                          className="btn-action delete hover:bg-rose-50 text-rose-600 border-rose-200"
                          onClick={() => promptDelete(attempt)}
                          title={attempt.status === 'DELETED' ? 'حذف نهائي من النظام' : 'نقل إلى الطلبات المحذوفة وتحرير المقعد'}
                          style={{ color: '#E5232A' }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards View (Section 80 Requirement) */}
          <div className="admin-responsive-cards-grid admin-mobile-cards-view">
            {visibleAttempts.map((attempt) => (
              <div
                key={attempt.id}
                className={`admin-payment-card-item ${
                  attempt.status === 'PENDING_VERIFICATION' ? 'row-pending' : ''
                }`}
              >
                <div className="admin-card-header">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span className="font-mono font-bold text-[#E5232A]">
                        {attempt.bookingCode || attempt.bookingGroupCode || (attempt.bookingId ? attempt.bookingId.slice(0, 8) : attempt.bookingGroupId ? attempt.bookingGroupId.slice(0, 8) : '—')}
                      </span>
                      {duplicateAttemptIds.has(attempt.id) && (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            background: '#FEF3C7',
                            color: '#92400E',
                            border: '1px solid #FCD34D',
                            borderRadius: '999px',
                            padding: '1px 6px',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            width: 'fit-content',
                          }}
                          title="يوجد أكثر من حجز مسجل لهذا الطالب في نفس تاريخ الرحلة (تكرار)"
                        >
                          ⚠️ تكرار محتمل
                        </span>
                      )}
                    </div>
                    {(attempt.bookingGroupId || (attempt as any).tripCount > 1) && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                          background: '#EFF6FF',
                          color: '#1D4ED8',
                          border: '1px solid #BFDBFE',
                          borderRadius: '999px',
                          padding: '1px 6px',
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          width: 'fit-content',
                        }}
                      >
                        🗂️ {(attempt as any).tripCount || 8} رحلات
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="seat-badge">
                      {(attempt.bookingGroupId || (attempt as any).tripCount > 1)
                        ? `${(attempt as any).tripCount || 8} مقاعد`
                        : `#${attempt.seatNumber || '—'}`}
                    </span>
                    {getStatusBadge(attempt.status)}
                  </div>
                </div>

                <div className="admin-card-body">
                  <div>
                    <span className="text-muted text-xs block">الطالب</span>
                    <strong>
                      {attempt.studentName && !isGenericStudentName(attempt.studentName)
                        ? attempt.studentName
                        : attempt.studentPhone
                        ? `طالب (${attempt.studentPhone})`
                        : 'طالب مسجل'}
                    </strong>
                    <span className="text-muted text-xs block">{attempt.studentPhone || '—'}</span>
                  </div>

                  <div>
                    <span className="text-muted text-xs block">المبلغ وطريقة الدفع</span>
                    <strong className="text-slate-800 block">{attempt.amount} ج.م</strong>
                    {attempt.discountAmount && attempt.discountAmount > 0 ? (
                      <span className="inline-block text-[10px] text-emerald-700 font-bold bg-emerald-50 px-1 py-0.5 rounded border border-emerald-200 mb-1">
                        خصم {attempt.discountAmount} ج
                      </span>
                    ) : null}
                    <span className="text-xs text-[#E5232A] font-semibold block">
                      {attempt.paymentMethod === 'INSTAPAY'
                        ? 'InstaPay'
                        : attempt.paymentMethod === 'WALLET'
                        ? attempt.paymentProvider || 'محفظة'
                        : 'الفرع'}
                    </span>
                  </div>

                  <div className="admin-card-body-full">
                    <span className="text-muted text-xs block">الجامعة والرحلة</span>
                    <span>{attempt.universityName || 'الجامعة'}</span>
                    <span className="text-muted text-xs mr-2">({attempt.tripDate || '—'})</span>
                  </div>

                  {attempt.transferFrom && (
                    <div className="admin-card-body-full">
                      <span className="text-muted text-xs block">رقم المحفظة / الحساب المحول منه</span>
                      <span className="font-mono text-xs">{attempt.transferFrom}</span>
                    </div>
                  )}

                  {/* Audit details in card */}
                  {attempt.status === 'PAID' && (attempt.verifiedByName || attempt.reviewedByName || attempt.verifiedAt || attempt.reviewedAt) && (
                    <div
                      className="admin-card-body-full"
                      style={{
                        background: '#F0FDF4',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #BBF7D0',
                        fontSize: '11px',
                        color: '#166534',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                        <span style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <UserCheck size={12} style={{ color: '#16A34A' }} />
                          اعتمد: {attempt.verifiedByName || attempt.reviewedByName || 'الإدارة'}
                        </span>
                        {(attempt.verifiedAt || attempt.reviewedAt) && (
                          <span style={{ fontSize: '10.5px', color: '#15803D' }}>
                            {formatAuditDateTime(attempt.verifiedAt || attempt.reviewedAt)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {attempt.status === 'REJECTED' && (attempt.rejectedByName || attempt.reviewedByName || attempt.rejectedAt || attempt.reviewedAt) && (
                    <div
                      className="admin-card-body-full"
                      style={{
                        background: '#FEF2F2',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #FECDD3',
                        fontSize: '11px',
                        color: '#991B1B',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                        <span style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <XCircle size={12} style={{ color: '#DC2626' }} />
                          رُفض: {attempt.rejectedByName || attempt.reviewedByName || 'الإدارة'}
                        </span>
                        {(attempt.rejectedAt || attempt.reviewedAt) && (
                          <span style={{ fontSize: '10.5px', color: '#B91C1C' }}>
                            {formatAuditDateTime(attempt.rejectedAt || attempt.reviewedAt)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="admin-card-actions">
                  <button
                    type="button"
                    className="btn-review-card"
                    onClick={() => openReview(attempt)}
                  >
                    <Eye size={16} />
                    <span>مراجعة الإثبات</span>
                  </button>

                  {/* Confirmed Booking WhatsApp Action Button */}
                  {attempt.status === 'PAID' && attempt.studentPhone && (
                    <a
                      href={getBookingConfirmationWhatsAppUrl(attempt)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-whatsapp-card"
                      title="إرسال تأكيد الحجز وبيانات الرحلة للطالب عبر واتساب"
                    >
                      <MessageSquare size={16} />
                      <span>واتساب</span>
                    </a>
                  )}

                  {(attempt.status === 'PENDING_VERIFICATION' ||
                    attempt.status === 'AWAITING_BRANCH_PAYMENT') && (
                    <>
                      <button
                        type="button"
                        className="btn-approve-card"
                        onClick={() => {
                          setSelectedAttempt(attempt);
                          setIsApproveConfirmOpen(true);
                        }}
                      >
                        <CheckCircle2 size={16} />
                        <span>اعتماد</span>
                      </button>

                      <button
                        type="button"
                        className="btn-reject-card"
                        onClick={() => {
                          setSelectedAttempt(attempt);
                          setRejectionReason(REJECTION_REASONS[0]);
                          setAdminNote('');
                          setIsRejectOpen(true);
                        }}
                      >
                        <XCircle size={16} />
                        <span>رفض</span>
                      </button>
                    </>
                  )}

                  <button
                    type="button"
                    className="btn-reject-card btn-delete-card"
                    onClick={() => promptDelete(attempt)}
                    style={{ borderColor: '#E5232A', color: '#E5232A' }}
                    title={attempt.status === 'DELETED' ? 'حذف نهائي من النظام' : 'نقل إلى الطلبات المحذوفة'}
                  >
                    <Trash2 size={16} />
                    <span>{attempt.status === 'DELETED' ? 'حذف نهائي' : 'نقل للمحذوفات'}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Load More Footer (15 initial + 10 more on button click) */}
          {filteredAttempts.length > 0 && (
            <AdminLoadMore
              currentCount={Math.min(visibleCount, filteredAttempts.length)}
              totalCount={filteredAttempts.length}
              itemName="عملية دفع"
              onLoadMore={() => setVisibleCount((prev) => prev + 10)}
              step={10}
              onShowAll={() => setVisibleCount(filteredAttempts.length)}
            />
          )}
        </>
      )}
    </div>

      {/* MODAL 1: DETAILED REVIEW SCREEN (Sections 31, 32) */}
      {isReviewOpen && selectedAttempt && (
        <div className="admin-modal-overlay" onClick={() => setIsReviewOpen(false)}>
          <div className="admin-review-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <h3>مراجعة تفاصيل الحجز والدفع</h3>
                <span className="booking-ref-badge font-mono">
                  كود: {selectedAttempt.bookingCode || selectedAttempt.bookingGroupCode || selectedAttempt.bookingId || selectedAttempt.bookingGroupId}
                </span>
              </div>
              <button className="modal-close-btn" onClick={() => setIsReviewOpen(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body-scrollable">
              <div className="review-columns-grid">
                {/* Column 1: Info Sections */}
                <div className="review-details-col">
                  {/* Booking Info Box */}
                  <div className="review-box">
                    <h4 className="review-box-title flex items-center gap-1.5">
                      <Ticket size={16} />
                      <span>بيانات الحجز والطالب</span>
                    </h4>
                    <div className="review-data-grid">
                      <div className="data-item">
                        <span className="data-label">اسم الطالب:</span>
                        <strong className="data-value">{selectedAttempt.studentName || '—'}</strong>
                      </div>
                      <div className="data-item">
                        <span className="data-label">رقم الهاتف:</span>
                        <span className="data-value font-mono">{selectedAttempt.studentPhone || '—'}</span>
                      </div>
                      <div className="data-item">
                        <span className="data-label">الجامعة:</span>
                        <strong className="data-value">{selectedAttempt.universityName || '—'}</strong>
                      </div>
                      <div className="data-item">
                        <span className="data-label">تاريخ الرحلة:</span>
                        <span className="data-value font-mono">{selectedAttempt.tripDate || '—'}</span>
                      </div>
                      <div className="data-item">
                        <span className="data-label">المقعد المحجوز:</span>
                        <span className="data-value seat-badge">
                          {(selectedAttempt.bookingGroupId || (selectedAttempt as any).tripCount > 1)
                            ? `${(selectedAttempt as any).tripCount || 8} مقاعد (مجموعة رحلات)`
                            : typeof selectedAttempt.seatNumber === 'number'
                            ? `رقم ${selectedAttempt.seatNumber}`
                            : selectedAttempt.seatNumber
                            ? String(selectedAttempt.seatNumber)
                            : '—'}
                        </span>
                      </div>
                      <div className="data-item">
                        <span className="data-label">المبلغ المطلوب:</span>
                        <div>
                          {reviewCalc.hasDiscount ? (
                            <>
                              <span style={{ textDecoration: 'line-through', color: '#94A3B8', fontSize: '0.8rem', marginLeft: '6px' }}>
                                {reviewCalc.originalTotal} ج.م
                              </span>
                              <strong className="data-value text-emerald-700 text-base font-bold font-mono">
                                {reviewCalc.finalTotal} ج.م
                              </strong>
                              <span style={{ display: 'block', fontSize: '0.7rem', color: '#059669', fontWeight: 700 }}>
                                (خصم إداري: {reviewCalc.discount} ج.م)
                              </span>
                            </>
                          ) : (
                            <strong className="data-value text-[#E5232A] text-base font-bold font-mono">
                              {selectedAttempt.amount || 0} ج.م
                            </strong>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Package Trips Breakdown Box */}
                  {groupData?.bookings && groupData.bookings.length > 0 && (
                    <div className="review-box" style={{ marginTop: '0.85rem' }}>
                      <h4 className="review-box-title flex items-center justify-between" style={{ marginBottom: '0.5rem' }}>
                        <span className="flex items-center gap-1.5">
                          <Ticket size={16} />
                          <span>رحلات الباقة المعتمدة ({groupData.bookings.length} رحلات)</span>
                        </span>
                        <span className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded font-bold">
                          إجمالي: {reviewCalc.hasDiscount ? reviewCalc.finalTotal : (groupData.totalAmount || selectedAttempt.amount)} ج.م
                        </span>
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '180px', overflowY: 'auto' }}>
                        {groupData.bookings.map((b: any, idx: number) => {
                          const isRet = isReturnTrip(b.direction) || (typeof b.tripId === 'string' && b.tripId.includes('_return_'));
                          const originText = b.origin || (isRet ? (b.universityName || 'الجامعة') : 'الإسكندرية');
                          const destText = b.destination || (isRet ? 'الإسكندرية' : (b.universityName || 'الجامعة'));
                          const displayTicketPrice = reviewCalc.hasDiscount ? reviewCalc.finalUnitPrice : b.price;

                          return (
                            <div
                              key={idx}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: '#F8FAFC',
                                padding: '0.45rem 0.65rem',
                                borderRadius: '8px',
                                fontSize: '0.78rem',
                                border: '1px solid #E2E8F0',
                              }}
                            >
                              <div>
                                <strong style={{ color: '#0F172A' }}>
                                  {b.date} ({isRet ? 'عودة للإسكندرية 🌙' : 'ذهاب للجامعة ☀️'})
                                </strong>
                                <span style={{ color: '#64748B', display: 'block', fontSize: '0.72rem' }}>
                                  {originText} ⬅️ {destText}
                                </span>
                              </div>
                              <div style={{ textAlign: 'left' }}>
                                <span className="seat-badge" style={{ fontSize: '0.72rem', padding: '1px 6px' }}>
                                  مقعد #{b.seatNumber}
                                </span>
                                <span style={{ display: 'block', color: reviewCalc.hasDiscount ? '#059669' : '#E5232A', fontWeight: 700, fontSize: '0.72rem' }}>
                                  {displayTicketPrice} ج.م
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Administrative Discount Box */}
                  <div
                    className="review-box"
                    style={{
                      marginTop: '0.85rem',
                      background: reviewCalc.hasDiscount ? '#F0FDF4' : '#F8FAFC',
                      border: reviewCalc.hasDiscount ? '1.5px solid #10B981' : '1px solid #E2E8F0',
                      borderRadius: '12px',
                      padding: '12px 14px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <h4
                        className="review-box-title"
                        style={{
                          margin: 0,
                          color: reviewCalc.hasDiscount ? '#065F46' : '#1E293B',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontWeight: 800,
                          fontSize: '0.86rem',
                        }}
                      >
                        <BadgePercent size={18} style={{ color: reviewCalc.hasDiscount ? '#059669' : '#475569' }} />
                        <span>خصم إداري على الطلب (يوزع بالتساوي على التذاكر)</span>
                      </h4>
                      <span
                        style={{
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: '9999px',
                          background: reviewCalc.hasDiscount ? '#DCFCE7' : '#F1F5F9',
                          color: reviewCalc.hasDiscount ? '#14532D' : '#64748B',
                          border: reviewCalc.hasDiscount ? '1px solid #86EFAC' : '1px solid #CBD5E1',
                        }}
                      >
                        {reviewCalc.ticketCount} تذاكر × {reviewCalc.originalUnitPrice} ج.م
                      </span>
                    </div>

                    {/* Interactive Discount Inputs for Pending / Branch Payments */}
                    {selectedAttempt.status === 'PENDING_VERIFICATION' ||
                    selectedAttempt.status === 'AWAITING_BRANCH_PAYMENT' ? (
                      <>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '8px' }}>
                          <div style={{ flex: '1 1 180px' }}>
                            <label style={{ display: 'block', fontSize: '0.74rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                              مبلغ الخصم الإجمالي على الطلب (ج.م):
                            </label>
                            <div style={{ position: 'relative' }}>
                              <input
                                type="number"
                                min="0"
                                max={reviewCalc.originalTotal}
                                value={discountAmount}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setDiscountAmount(val === '' ? '' : Math.max(0, Number(val)));
                                }}
                                placeholder="مثال: 100"
                                style={{
                                  width: '100%',
                                  padding: '7px 10px 7px 38px',
                                  borderRadius: '8px',
                                  border: reviewCalc.isExceeding ? '1.5px solid #EF4444' : '1.5px solid #CBD5E1',
                                  fontSize: '0.9rem',
                                  fontWeight: 800,
                                  outline: 'none',
                                  backgroundColor: '#FFFFFF',
                                  fontFamily: 'monospace',
                                  color: '#0F172A',
                                }}
                              />
                              <span
                                style={{
                                  position: 'absolute',
                                  left: '10px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  fontSize: '0.75rem',
                                  color: '#64748B',
                                  fontWeight: 700,
                                }}
                              >
                                ج.م
                              </span>
                            </div>
                          </div>

                          {/* Quick Chips */}
                          <div style={{ flex: '1 1 auto' }}>
                            <span style={{ display: 'block', fontSize: '0.72rem', color: '#64748B', fontWeight: 600, marginBottom: '4px' }}>
                              خيارات سريعة:
                            </span>
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {[0, 25, 50, 100, 150, 200].map((preset) => (
                                <button
                                  key={preset}
                                  type="button"
                                  onClick={() => setDiscountAmount(preset === 0 ? '' : preset)}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '0.72rem',
                                    fontWeight: 700,
                                    borderRadius: '6px',
                                    border:
                                      (discountAmount === preset || (preset === 0 && discountAmount === ''))
                                        ? '1.5px solid #2563EB'
                                        : '1px solid #CBD5E1',
                                    background:
                                      (discountAmount === preset || (preset === 0 && discountAmount === ''))
                                        ? '#EFF6FF'
                                        : '#FFFFFF',
                                    color:
                                      (discountAmount === preset || (preset === 0 && discountAmount === ''))
                                        ? '#1D4ED8'
                                        : '#334155',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                  }}
                                >
                                  {preset === 0 ? 'إلغاء الخصم' : `${preset} ج`}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {reviewCalc.isExceeding && (
                          <div style={{ color: '#DC2626', fontSize: '0.74rem', fontWeight: 700, margin: '6px 0', background: '#FEF2F2', padding: '6px 10px', borderRadius: '6px', border: '1px solid #FECDD3' }}>
                            ⚠️ تنبيه: قيمة الخصم ({discountAmount} ج.م) أكبر من إجمالي مبلغ الحجز ({reviewCalc.originalTotal} ج.م)!
                          </div>
                        )}

                        {/* Breakdown Result Card */}
                        {reviewCalc.hasDiscount && !reviewCalc.isExceeding && (
                          <div
                            style={{
                              background: '#ECFDF5',
                              border: '1px solid #A7F3D0',
                              borderRadius: '8px',
                              padding: '10px 12px',
                              fontSize: '0.8rem',
                              marginTop: '6px',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                              <span style={{ color: '#065F46' }}>الخصم الموزع بالتساوي:</span>
                              <strong style={{ color: '#047857', fontFamily: 'monospace' }}>
                                خصم {reviewCalc.discountPerTicket} ج.م لكل تذكرة ({reviewCalc.ticketCount} تذاكر)
                              </strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                              <span style={{ color: '#065F46' }}>سعر التذكرة بعد الخصم:</span>
                              <strong style={{ color: '#047857', fontFamily: 'monospace' }}>
                                {reviewCalc.finalUnitPrice} ج.م{' '}
                                <span style={{ textDecoration: 'line-through', color: '#94A3B8', fontSize: '0.72rem', marginRight: '4px' }}>
                                  ({reviewCalc.originalUnitPrice} ج.م)
                                </span>
                              </strong>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderTop: '1px dashed #6EE7B7',
                                paddingTop: '6px',
                                marginTop: '6px',
                              }}
                            >
                              <span style={{ color: '#065F46', fontWeight: 800 }}>
                                المبلغ الصافي المعتمد والمدرج بالخزينة:
                              </span>
                              <strong style={{ color: '#047857', fontSize: '0.96rem', fontFamily: 'monospace' }}>
                                {reviewCalc.finalTotal} ج.م
                              </strong>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      /* Display read-only info if already approved */
                      <div style={{ fontSize: '0.8rem' }}>
                        {selectedAttempt.discountAmount && selectedAttempt.discountAmount > 0 ? (
                          <div
                            style={{
                              background: '#ECFDF5',
                              border: '1px solid #A7F3D0',
                              borderRadius: '8px',
                              padding: '8px 12px',
                              color: '#065F46',
                            }}
                          >
                            <div style={{ marginBottom: '2px' }}>
                              تم تطبيق خصم إداري معتمد:{' '}
                              <strong style={{ color: '#047857' }}>{selectedAttempt.discountAmount} ج.م</strong> بالتساوي{' '}
                              (بواقع {selectedAttempt.discountPerTicket || Math.round(selectedAttempt.discountAmount / reviewCalc.ticketCount)} ج.م لكل تذكرة).
                            </div>
                            <div>
                              المبلغ المدرج بالخزينة:{' '}
                              <strong style={{ color: '#047857', fontSize: '0.9rem' }}>{selectedAttempt.amount} ج.م</strong>{' '}
                              (بدلاً من {selectedAttempt.originalAmount || (selectedAttempt.amount + selectedAttempt.discountAmount)} ج.م).
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: '#64748B' }}>
                            لم يتم تطبيق خصم إداري على هذا الطلب (تم التحصيل بالمبلغ الكامل: {selectedAttempt.amount} ج.م).
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Payment Details Box */}
                  <div className="review-box">
                    <h4 className="review-box-title flex items-center gap-1.5">
                      <CreditCard size={16} />
                      <span>بيانات التحويل المسجلة</span>
                    </h4>
                    <div className="review-data-grid">
                      <div className="data-item">
                        <span className="data-label">طريقة الدفع:</span>
                        <span className="data-value font-bold">
                          {selectedAttempt.paymentMethod === 'INSTAPAY'
                            ? 'InstaPay'
                            : selectedAttempt.paymentMethod === 'WALLET'
                            ? `محفظة (${selectedAttempt.paymentProvider || 'إلكترونية'})`
                            : 'سداد بالفرع'}
                        </span>
                      </div>
                      <div className="data-item">
                        <span className="data-label">الرقم المحول منه:</span>
                        <strong className="data-value font-mono text-emerald-700">
                          {selectedAttempt.transferFrom || '—'}
                        </strong>
                      </div>
                      {selectedAttempt.transferReference && (
                        <div className="data-item">
                          <span className="data-label">رقم العملية / المرجع:</span>
                          <span className="data-value font-mono">{selectedAttempt.transferReference}</span>
                        </div>
                      )}
                      <div className="data-item">
                        <span className="data-label">تاريخ ووقت الإرسال:</span>
                        <span className="data-value">
                          {selectedAttempt.submittedAt
                            ? new Date(selectedAttempt.submittedAt).toLocaleString('ar-EG')
                            : '—'}
                        </span>
                      </div>
                      <div className="data-item">
                        <span className="data-label">حالة الطلب الحالية:</span>
                        <span className="data-value">{getStatusBadge(selectedAttempt.status)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Approved Audit Card in Review Modal */}
                  {selectedAttempt.status === 'PAID' && (
                    <div className="review-box" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', marginTop: '0.85rem' }}>
                      <h4 className="review-box-title" style={{ color: '#166534', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                        <CheckCircle2 size={16} style={{ color: '#16A34A' }} />
                        <span>بيانات اعتماد الطلب وتأكيد السداد</span>
                      </h4>
                      <div className="review-data-grid">
                        <div className="data-item">
                          <span className="data-label">الموظف / الأدمن المعتمد:</span>
                          <strong className="data-value" style={{ color: '#15803D', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <UserCheck size={14} />
                            {selectedAttempt.verifiedByName || selectedAttempt.reviewedByName || 'إدارة النظام'}
                          </strong>
                        </div>
                        <div className="data-item">
                          <span className="data-label">تاريخ ووقت الاعتماد:</span>
                          <span className="data-value font-mono" style={{ color: '#166534', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Clock size={13} />
                            {formatAuditDateTime(selectedAttempt.verifiedAt || selectedAttempt.reviewedAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rejected Audit Card in Review Modal */}
                  {selectedAttempt.status === 'REJECTED' && (
                    <div className="review-box" style={{ background: '#FEF2F2', border: '1px solid #FECDD3', marginTop: '0.85rem' }}>
                      <h4 className="review-box-title" style={{ color: '#991B1B', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                        <XCircle size={16} style={{ color: '#DC2626' }} />
                        <span>بيانات رفض الطلب</span>
                      </h4>
                      <div className="review-data-grid">
                        <div className="data-item">
                          <span className="data-label">الموظف / الأدمن الذي قام بالرفض:</span>
                          <strong className="data-value" style={{ color: '#B91C1C', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <XCircle size={14} />
                            {selectedAttempt.rejectedByName || selectedAttempt.reviewedByName || 'إدارة النظام'}
                          </strong>
                        </div>
                        <div className="data-item">
                          <span className="data-label">تاريخ ووقت الرفض:</span>
                          <span className="data-value font-mono" style={{ color: '#991B1B', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Clock size={13} />
                            {formatAuditDateTime(selectedAttempt.rejectedAt || selectedAttempt.reviewedAt)}
                          </span>
                        </div>
                        {selectedAttempt.rejectionReason && (
                          <div className="data-item" style={{ gridColumn: 'span 2' }}>
                            <span className="data-label">سبب الرفض المسجل:</span>
                            <strong className="data-value text-rose-700">{selectedAttempt.rejectionReason}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Destination Snapshot Box */}
                  {selectedAttempt.destinationSnapshot && (
                    <div className="review-box destination-box">
                      <h4 className="review-box-title text-xs text-slate-500">
                        الحساب المستهدف وقت إنشاء الطلب (Snapshot)
                      </h4>
                      <div className="text-xs text-slate-600 space-y-1">
                        <div>
                          <strong>المستفيد:</strong> {selectedAttempt.destinationSnapshot.recipientName}
                        </div>
                        <div>
                          <strong>الحساب/الرقم:</strong>{' '}
                          <span className="font-mono">{selectedAttempt.destinationSnapshot.recipientAccount}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rejection / Note History if already reviewed */}
                  {selectedAttempt.adminNote && (
                    <div className="review-box note-box">
                      <h4 className="review-box-title text-amber-700">ملاحظات سابقة:</h4>
                      <p className="text-sm text-slate-700">{selectedAttempt.adminNote}</p>
                    </div>
                  )}
                </div>

                {/* Column 2: Payment Proof Image Showcase (Section 31) */}
                <div className="review-proof-col">
                  {(() => {
                    const effectiveProofUrl =
                      selectedAttempt.proofImageUrl ||
                      (selectedAttempt as any).proofImageBase64 ||
                      null;

                    return (
                      <>
                        <div className="proof-header-row">
                          <span className="font-bold text-sm">صورة إثبات التحويل</span>
                          {effectiveProofUrl && (
                            <div className="zoom-controls">
                              <button
                                type="button"
                                className="zoom-btn"
                                onClick={() => setZoomLevel((z) => Math.max(0.5, z - 0.25))}
                                title="تصغير"
                              >
                                <span style={{ fontWeight: 800, fontSize: '13px' }}>-</span>
                              </button>
                              <span className="zoom-indicator">{Math.round(zoomLevel * 100)}%</span>
                              <button
                                type="button"
                                className="zoom-btn"
                                onClick={() => setZoomLevel((z) => Math.min(3, z + 0.25))}
                                title="تكبير"
                              >
                                <span style={{ fontWeight: 800, fontSize: '13px' }}>+</span>
                              </button>
                              <a
                                href={effectiveProofUrl}
                                target="_blank"
                                rel="noreferrer"
                                download={effectiveProofUrl.startsWith('data:') ? 'payment_proof.jpg' : undefined}
                                className="zoom-btn"
                                title="فتح في نافذة كاملة"
                              >
                                <ExternalLink size={15} />
                              </a>
                            </div>
                          )}
                        </div>

                        <div className="proof-image-viewport">
                          {effectiveProofUrl ? (
                            <div
                              className="proof-image-wrapper"
                              style={{ transform: `scale(${zoomLevel})` }}
                            >
                              <img
                                src={effectiveProofUrl}
                                alt="إثبات التحويل"
                                className="proof-img"
                              />
                            </div>
                          ) : (
                            <div className="no-proof-placeholder">
                              <Building2 size={42} className="text-slate-300" />
                              <p>لا توجد صورة إثبات مرفقة (طريقة الدفع: {selectedAttempt.paymentMethod})</p>
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Modal Footer with Actions */}
            <div className="modal-footer">
              <div className="footer-status-info">
                <span>تنبيه: اعتماد الدفع يؤكد الحجز فورا ويصدر التذكرة الرقمية للطالب.</span>
              </div>
              <div className="footer-buttons-group">
                <button
                  type="button"
                  className="admin-btn secondary"
                  onClick={() => setIsReviewOpen(false)}
                >
                  إغلاق
                </button>

                {selectedAttempt.status === 'PAID' && selectedAttempt.studentPhone && (
                  <a
                    href={getBookingConfirmationWhatsAppUrl(selectedAttempt, groupData)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="admin-btn secondary flex items-center gap-1.5"
                    style={{
                      backgroundColor: '#ECFDF5',
                      border: '1.5px solid #10B981',
                      color: '#047857',
                      fontWeight: 700,
                      textDecoration: 'none',
                    }}
                    title="إرسال تأكيد الحجز والبيانات للطالب عبر واتساب"
                  >
                    <MessageSquare size={16} />
                    <span>إرسال تأكيد الحجز (واتساب)</span>
                  </a>
                )}

                <button
                  type="button"
                  className="admin-btn secondary flex items-center gap-1.5"
                  onClick={() => handleOpenEditBooking(selectedAttempt)}
                  disabled={isPreparingEdit}
                  style={{
                    backgroundColor: '#FFFBEB',
                    border: '1.5px solid #F59E0B',
                    color: '#B45309',
                    fontWeight: 700,
                  }}
                  title="تعديل تفاصيل الحجز، المقاعد، والمواعيد للطالب في واجهة الحجز الإداري المباشر"
                >
                  <Edit size={16} />
                  <span>{isPreparingEdit ? 'جاري فتح التعديل...' : 'تعديل الحجز والرحلات'}</span>
                </button>

                <button
                  type="button"
                  className="admin-btn secondary flex items-center gap-1.5"
                  onClick={() => promptDelete(selectedAttempt)}
                  style={{ color: '#E5232A' }}
                >
                  <Trash2 size={15} />
                  <span>{selectedAttempt.status === 'DELETED' ? 'حذف نهائي من النظام' : 'نقل إلى الطلبات المحذوفة'}</span>
                </button>

                {(selectedAttempt.status === 'PENDING_VERIFICATION' ||
                  selectedAttempt.status === 'AWAITING_BRANCH_PAYMENT') && (
                  <>
                    <button
                      type="button"
                      className="admin-btn reject-btn flex items-center gap-1.5"
                      onClick={() => {
                        setRejectionReason(REJECTION_REASONS[0]);
                        setAdminNote('');
                        setIsRejectOpen(true);
                      }}
                    >
                      <XCircle size={16} />
                      <span>رفض التحويل</span>
                    </button>

                    <button
                      type="button"
                      className="admin-btn approve-btn flex items-center gap-1.5"
                      onClick={() => setIsApproveConfirmOpen(true)}
                    >
                      <CheckCircle2 size={16} />
                      <span>اعتماد الدفع وتأكيد الحجز</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: CONFIRM APPROVAL MODAL (Section 77) */}
      {isApproveConfirmOpen && selectedAttempt && (
        <div className="admin-modal-overlay alert-modal-overlay">
          <div className="admin-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon-box approve">
              <CheckCircle2 size={36} className="text-emerald-600" />
            </div>

            <h3 className="confirm-title">تأكيد اعتماد التحويل؟</h3>

            <p className="confirm-desc">
              أنت على وشك تأكيد سداد الحجز رقم <strong>{selectedAttempt.bookingCode || selectedAttempt.bookingGroupCode || selectedAttempt.bookingId}</strong> بمبلغ{' '}
              <strong style={{ color: '#047857', fontSize: '1.05rem' }}>{reviewCalc.finalTotal} ج.م</strong>.
              {reviewCalc.hasDiscount && (
                <span style={{ display: 'block', fontSize: '0.8rem', color: '#065F46', marginTop: '6px', background: '#ECFDF5', padding: '6px 10px', borderRadius: '6px', border: '1px solid #A7F3D0' }}>
                  🏷️ يشمل خصم إداري بقيمة <strong>{reviewCalc.discount} ج.م</strong> بالتساوي بواقع <strong>{reviewCalc.discountPerTicket} ج.م</strong> لكل تذكرة من الـ <strong>{reviewCalc.ticketCount} تذاكر</strong> (ليصبح سعر التذكرة {reviewCalc.finalUnitPrice} ج.م).
                </span>
              )}
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#F0FDF4', padding: '8px 12px', borderRadius: '8px', border: '1px solid #BBF7D0', margin: '10px 0', fontSize: '12px', color: '#166534' }}>
              <UserCheck size={16} className="text-emerald-600 flex-shrink-0" />
              <span>الموظف / الأدمن المنفذ للاعتماد: <strong>{currentAdminName}</strong></span>
            </div>

            <div className="confirm-checklist">
              <div className="check-item">
                <CheckCircle2 size={16} className="text-emerald-600" />
                <span>اعتماد الدفع وتغيير الحالة إلى PAID</span>
              </div>
              <div className="check-item">
                <CheckCircle2 size={16} className="text-emerald-600" />
                <span>تأكيد الحجز رسميًا وتثبيت المقاعد للطالب</span>
              </div>
              <div className="check-item">
                <CheckCircle2 size={16} className="text-emerald-600" />
                <span>إدراج المبلغ الصافي المعتمد ({reviewCalc.finalTotal} ج.م) في سجل الخزينة والحسابات مباشرة</span>
              </div>
              <div className="check-item">
                <CheckCircle2 size={16} className="text-emerald-600" />
                <span>إصدار التذاكر الرقمية المعتمدة بسعر {reviewCalc.finalUnitPrice} ج.م للتذكرة مع رمز QR</span>
              </div>
              <div className="check-item">
                <CheckCircle2 size={16} className="text-emerald-600" />
                <span>إرسال إشعار فوري لحساب الطالب بتأكيد السداد</span>
              </div>
            </div>

            {/* Modal Error Alert inside approval modal */}
            {modalActionError && (
              <div style={{
                background: '#FEF2F2',
                border: '1px solid #FCA5A5',
                color: '#991B1B',
                padding: '10px 14px',
                borderRadius: '8px',
                fontSize: '12px',
                lineHeight: '1.5',
                margin: '12px 0',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <strong style={{ display: 'block', marginBottom: '2px' }}>تعذر اعتماد السداد:</strong>
                  <span>{modalActionError}</span>
                </div>
              </div>
            )}

            <div className="confirm-modal-buttons">
              <button
                type="button"
                className="admin-btn approve-btn w-full"
                onClick={handleApprove}
                disabled={actionLoading}
              >
                {actionLoading ? 'جاري الاعتماد...' : 'نعم، تأكيد واعتماد الدفع'}
              </button>
              <button
                type="button"
                className="admin-btn secondary w-full"
                onClick={() => {
                  setIsApproveConfirmOpen(false);
                  setModalActionError(null);
                }}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: REJECTION MODAL WITH MANDATORY REASON (Sections 36, 78) */}
      {isRejectOpen && selectedAttempt && (
        <div
          className="admin-modal-overlay alert-modal-overlay"
          onClick={() => {
            if (!actionLoading) {
              setIsRejectOpen(false);
              setModalActionError(null);
            }
          }}
        >
          <div className="admin-reject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon-box reject">
              <XCircle size={36} className="text-rose-600" />
            </div>

            <h3 className="confirm-title">رفض إثبات التحويل</h3>

            <p className="confirm-desc">
              حدد سبب عدم قبول إثبات التحويل للطلب رقم <strong>{selectedAttempt.bookingCode || selectedAttempt.bookingId}</strong>. سيتلقى الطالب السبب ليتمكن من إعادة إرسال الإثبات دون فقد مقعده.
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#FEF2F2', padding: '8px 12px', borderRadius: '8px', border: '1px solid #FECDD3', margin: '10px 0', fontSize: '12px', color: '#991B1B' }}>
              <XCircle size={16} className="text-rose-600 flex-shrink-0" />
              <span>الموظف / الأدمن المنفذ للرفض: <strong>{currentAdminName}</strong></span>
            </div>

            {/* Inner Modal Error if operation fails */}
            {modalActionError && (
              <div className="modal-inner-error-banner" style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#B91C1C', padding: '10px 14px', borderRadius: '8px', marginBottom: '12px', fontSize: '0.85rem' }}>
                <AlertTriangle size={18} style={{ flexShrink: 0 }} />
                <span>{modalActionError}</span>
              </div>
            )}

            {/* Reasons Picker */}
            <div className="reasons-radio-list">
              <span className="reasons-title">سبب الرفض (إلزامي):</span>
              {REJECTION_REASONS.map((reason) => {
                const isSelected = rejectionReason === reason;
                return (
                  <label
                    key={reason}
                    className={`reason-label-row ${isSelected ? 'selected' : ''}`}
                    onClick={() => setRejectionReason(reason)}
                  >
                    <div className={`custom-radio-circle ${isSelected ? 'checked' : ''}`}>
                      {isSelected && <span className="custom-radio-dot" />}
                    </div>
                    <span className="reason-label-text">{reason}</span>
                    {isSelected && <span className="selected-badge">محدد</span>}
                  </label>
                );
              })}
            </div>

            {/* Optional Admin Note */}
            <div className="admin-note-input-group">
              <label className="note-label">ملاحظة توضيحية إضافية للطالب (اختياري):</label>
              <textarea
                className="note-textarea"
                rows={2}
                placeholder="مثال: يرجى رفع صورة واضحة توضح رقم المعاملة والمبلغ بالكامل..."
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
              />
            </div>

            <div className="confirm-modal-buttons">
              <button
                type="button"
                className="admin-btn reject-btn w-full"
                onClick={handleReject}
                disabled={actionLoading}
              >
                {actionLoading ? 'جاري تسجيل الرفض...' : 'تأكيد رفض الإثبات وإشعار الطالب'}
              </button>
              <button
                type="button"
                className="admin-btn secondary w-full"
                onClick={() => {
                  setIsRejectOpen(false);
                  setModalActionError(null);
                }}
                disabled={actionLoading}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 4: PURGE & DELETE RESERVATION CONFIRMATION */}
      {isDeleteConfirmOpen && attemptToDelete && (
        <div
          className="admin-modal-overlay alert-modal-overlay"
          onClick={() => {
            if (!actionLoading) {
              setIsDeleteConfirmOpen(false);
              setAttemptToDelete(null);
            }
          }}
        >
          <div className="admin-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon-box reject" style={{ background: '#FEE2E2', color: '#E5232A' }}>
              <Trash2 size={36} className="text-[#E5232A]" />
            </div>

            <h3 className="confirm-title" style={{ color: '#991B1B' }}>
              {attemptToDelete.status === 'DELETED'
                ? 'تأكيد الحذف النهائي لسجل الدفع؟'
                : 'تأكيد نقل الطلب إلى الطلبات المحذوفة؟'}
            </h3>

            <p className="confirm-desc">
              {attemptToDelete.status === 'DELETED' ? (
                <>
                  أنت على وشك حذف الحجز رقم{' '}
                  <strong className="font-mono text-[#E5232A]">
                    {attemptToDelete.bookingCode || attemptToDelete.bookingGroupCode || attemptToDelete.bookingId || attemptToDelete.id}
                  </strong>{' '}
                  نهائياً من قاعدة البيانات. هذا الإجراء دائم ولا يمكن التراجع عنه.
                </>
              ) : (
                <>
                  أنت على وشك إلغاء الحجز رقم{' '}
                  <strong className="font-mono text-[#E5232A]">
                    {attemptToDelete.bookingCode || attemptToDelete.bookingGroupCode || attemptToDelete.bookingId || attemptToDelete.id}
                  </strong>{' '}
                  للطالب <strong>{attemptToDelete.studentName && !isGenericStudentName(attemptToDelete.studentName) ? attemptToDelete.studentName : (attemptToDelete.studentPhone ? `طالب (${attemptToDelete.studentPhone})` : 'طالب مسجل')}</strong> ونقله إلى قسم <strong>الطلبات المحذوفة</strong>.
                </>
              )}
            </p>

            <div className="confirm-checklist" style={{ background: '#FFF1F2', borderColor: '#FECDD3' }}>
              <div className="check-item">
                <CheckCircle2 size={16} className="text-[#E5232A]" />
                <span>
                  تحرير المقعد (
                  {typeof attemptToDelete.seatNumber === 'number'
                    ? `#${attemptToDelete.seatNumber}`
                    : attemptToDelete.seatNumber
                    ? String(attemptToDelete.seatNumber)
                    : 'المحجوز'}
                  ) في الحافلة فوراً ليصبح متاحاً للطلاب الآخرين
                </span>
              </div>
              <div className="check-item">
                <Trash2 size={16} className="text-[#E5232A]" />
                <span>
                  {attemptToDelete.status === 'DELETED'
                    ? 'إزالة السجل نهائياً وتفريغ المساحة'
                    : 'نقل الطلب لقسم (الطلبات المحذوفة) مع الحفاظ على أرشيف الإثبات والبيانات'}
                </span>
              </div>
            </div>

            <div className="confirm-modal-buttons">
              <button
                type="button"
                className="admin-btn w-full"
                style={{ background: '#E5232A', color: '#fff', borderColor: '#E5232A' }}
                onClick={() => handleDeleteAttempt(attemptToDelete, attemptToDelete.status === 'DELETED')}
                disabled={actionLoading}
              >
                {actionLoading
                  ? 'جاري التنفيذ...'
                  : attemptToDelete.status === 'DELETED'
                  ? 'نعم، حذف نهائي من النظام'
                  : 'نعم، نقل للطلبات المحذوفة وتحرير المقعد'}
              </button>
              {attemptToDelete.status !== 'DELETED' && (
                <button
                  type="button"
                  className="admin-btn secondary w-full text-xs"
                  style={{
                    background: '#FFF1F2',
                    color: '#991B1B',
                    borderColor: '#FECDD3',
                    fontWeight: 700,
                  }}
                  onClick={() => handleDeleteAttempt(attemptToDelete, true)}
                  disabled={actionLoading}
                  title="حذف هذا السجل نهائياً من قاعدة البيانات (مفيد في حالة التكرار المالي والخطأ)"
                >
                  حذف نهائي فوري من النظام (لسجلات التكرار والخطأ)
                </button>
              )}
              <button
                type="button"
                className="admin-btn secondary w-full"
                onClick={() => {
                  setIsDeleteConfirmOpen(false);
                  setAttemptToDelete(null);
                }}
                disabled={actionLoading}
              >
                إلغاء التراجع
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 5: DIRECT BOOKING / EDIT MODAL FOR STUDENT */}
      {isEditBookingOpen && selectedAttempt && (
        <AdminDirectBookingModal
          students={allStudents}
          trips={allTrips}
          universities={allUniversities}
          defaultDate={editInitialDates[0]}
          defaultStudentUid={editInitialStudent?.uid}
          initialStudent={editInitialStudent}
          initialTripBookingMode={editInitialQueue.length > 0 ? 'ROUND_TRIP' : 'ONE_WAY_OUTBOUND'}
          initialSelectedDates={editInitialDates}
          initialQueue={editInitialQueue}
          initialSelectedTripId={editInitialTripId}
          initialSelectedSeatNumber={editInitialSeatNumber}
          initialPaymentMethod={editInitialPaymentMethod}
          initialIsPaid={selectedAttempt.status === 'PAID'}
          initialAdminNote={`تعديل على حجز الطالب (${selectedAttempt.bookingCode || selectedAttempt.bookingGroupCode || ''})`}
          modalTitle={`تعديل حجز وتذاكر الطالب: ${selectedAttempt.studentName || 'طالب مسجل'}`}
          modalSubtitle="تعديل المواعيد، خطوط السير، المقاعد، وطريقة السداد مع تحديث بيانات الطالب الأصلية وحذف السجل القديم تلقائياً لمنع التكرار"
          editingAttempt={selectedAttempt}
          onClose={() => {
            setIsEditBookingOpen(false);
          }}
          onSuccess={(msg) => {
            setIsEditBookingOpen(false);
            setIsReviewOpen(false);
            setActionSuccess(msg || 'تم تحديث وتعديل حجز الطالب بنجاح!');
            loadAttempts();
            setTimeout(() => setActionSuccess(null), 5000);
          }}
        />
      )}
    </div>
  );
};

export default AdminPaymentVerification;
